import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  ADD_TASK_NOTE,
  backoffSummary,
  backoffTransition,
  BUDGETS,
  budgetLabel,
  budgetRequestLabels,
  budgetStateLabel,
  budgetSummary,
  budgetTransition,
  CACHE_CHANGES,
  CACHE_SUGGESTIONS,
  cacheChangeLabels,
  cacheSummary,
  cacheTotals,
  cacheTransition,
  canAskCache,
  countedAt,
  fallbackPanel,
  fallbackSummary,
  fallbackTransition,
  formatCount,
  initialBackoff,
  initialBudget,
  initialCache,
  initialFallback,
  LATER_MINUTES,
  MAX_ATTEMPTS,
  modelStatus,
  PEOPLE,
  PROVIDER_SCRIPTS,
  providerScriptLabels,
  recentFailures,
  statusJson,
  toBudget,
  toCacheChange,
  toLaterMinutes,
  toPerson,
  toProviderScript,
  waitLabel,
  yesNo,
  type BackoffAction,
  type BudgetAction,
  type CacheAction,
  type FallbackAction,
} from './ops-models';

import './relationship-labs.css';
import './ops-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
  className = '',
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
  className?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className={`rel-table cb-record-grid ${className}`} role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column, index) => (
            <span key={index} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const bullets = (titles: readonly string[]) =>
  titles.length ? titles.map((title) => `- ${title}`).join('\n') : '(none)';
const REPLAY = 'This graphic replays real requests.';
const CACHE_REPLAY = 'This graphic replays real requests, except two stand-in hashes.';

export function OpsCacheLab() {
  const [state, setState] = useState(initialCache);
  const act = (action: CacheAction) => setState((current) => cacheTransition(current, action));
  const totals = cacheTotals(state.rows);
  const summary = cacheSummary(state);
  return (
    <Lab
      title="What does a repeated question cost?"
      eyebrow="Operations · the log as a cache"
      className="rel-lab ops-lab"
    >
      <Predict>
        What does the second ask cost? Press Ask note 1 twice, then change What changed since the
        last ask.
      </Predict>
      <div className="cb-controls">
        <Button size="sm" disabled={!canAskCache(state)} onClick={() => act({ type: 'ask' })}>
          Ask note 1
        </Button>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.cacheOn}
          onClick={() => act({ type: 'toggle-cache' })}
        >
          Cache on
        </Button>
        <label>
          What changed since the last ask
          <ModelSelect
            aria-label="What changed since the last ask"
            value={state.change}
            onValueChange={(value) => act({ type: 'change', value: toCacheChange(value) })}
            options={CACHE_CHANGES.map((change) => ({
              value: change,
              label: cacheChangeLabels[change],
            }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Asks"
        className="ops-wide"
        columns={[
          'Ask',
          'Version',
          'Cached',
          'Prompt tokens',
          'Completion tokens',
          'Latency ms',
          'Hash',
        ]}
        template="minmax(0, 0.5fr) minmax(0, 1.1fr) minmax(0, 0.7fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 0.9fr) minmax(0, 1.2fr)"
        rows={state.rows.map((row) => [
          row.ask,
          row.version,
          yesNo(row.cached),
          row.promptTokens,
          row.completionTokens,
          formatCount(row.latencyMs),
          row.hash,
        ])}
      />
      <Panel label="Suggestions, the same every time">
        <pre aria-label="Suggestions">
          {state.rows.length ? bullets(CACHE_SUGGESTIONS) : '(nothing asked yet)'}
        </pre>
      </Panel>
      {state.rows.length ? (
        <Facts
          values={[
            ['Tokens spent', formatCount(totals.tokens)],
            ['Cost (example rate)', totals.cost],
            ['Hit rate', totals.hitRate],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{CACHE_REPLAY}</p>
    </Lab>
  );
}

export function OpsBudgetLab() {
  const [state, setState] = useState(initialBudget);
  const act = (action: BudgetAction) => setState((current) => budgetTransition(current, action));
  const summary = budgetSummary(state);
  return (
    <Lab
      title="When does the day's budget run out, and who is stopped?"
      eyebrow="Operations · the log as a meter"
      className="rel-lab ops-lab"
    >
      <Predict>
        How many asks does Maya get before a 429? Press Ask twice, then change Who asks to Ravi.
      </Predict>
      <div className="cb-controls">
        <label>
          Daily budget per person
          <ModelSelect
            aria-label="Daily budget per person"
            value={String(state.budget)}
            onValueChange={(value) => act({ type: 'budget', value: toBudget(value) })}
            options={BUDGETS.map((budget) => ({
              value: String(budget),
              label: budgetLabel(budget),
            }))}
          />
        </label>
        <label>
          Who asks
          <ModelSelect
            aria-label="Who asks"
            value={state.person}
            onValueChange={(value) => act({ type: 'person', value: toPerson(value) })}
            options={PEOPLE.map((person) => ({ value: person, label: person }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => act({ type: 'request', value: 'ask' })}>
          {budgetRequestLabels.ask}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => act({ type: 'request', value: 'digest' })}
        >
          {budgetRequestLabels.digest}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => act({ type: 'request', value: 'cached' })}
        >
          {budgetRequestLabels.cached}
        </Button>
      </div>
      <Table
        label="Tokens today"
        columns={['Person', 'Used today', 'Budget', 'State']}
        rows={PEOPLE.map((person) => [
          person,
          formatCount(state.used[person]),
          formatCount(state.budget),
          budgetStateLabel(state.used[person], state.budget),
        ])}
      />
      <Table
        label="Answers"
        className="ops-wide"
        columns={['Who', 'Request', 'Status', 'Tokens', 'Answer']}
        template="minmax(0, 0.7fr) minmax(0, 1.2fr) minmax(0, 0.6fr) minmax(0, 0.6fr) minmax(0, 3fr)"
        rows={state.log.map((entry) => [
          entry.person,
          budgetRequestLabels[entry.request],
          entry.status,
          entry.tokens,
          entry.answer,
        ])}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function OpsBackoffLab() {
  const [state, setState] = useState(initialBackoff);
  const act = (action: BackoffAction) => setState((current) => backoffTransition(current, action));
  const summary = backoffSummary(state);
  const run = state.run;
  return (
    <Lab
      title="What does the retry policy do with a bad afternoon?"
      eyebrow="Operations · the retry policy"
      className="rel-lab ops-lab"
    >
      <Predict>
        How long does the policy wait before giving up? Pick an option in The provider answers, then
        press Run the policy.
      </Predict>
      <div className="cb-controls">
        <label>
          The provider answers
          <ModelSelect
            aria-label="The provider answers"
            value={state.script}
            onValueChange={(value) => act({ type: 'script', value: toProviderScript(value) })}
            options={PROVIDER_SCRIPTS.map((script) => ({
              value: script,
              label: providerScriptLabels[script],
            }))}
          />
        </label>
        <Button size="sm" disabled={run !== null} onClick={() => act({ type: 'run' })}>
          Run the policy
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Attempts"
        columns={['Attempt', 'Answer', 'Wait after']}
        rows={(run?.attempts ?? []).map((attempt) => [
          attempt.attempt,
          String(attempt.answer),
          waitLabel(attempt.waitAfter),
        ])}
      />
      {run ? (
        <Facts
          values={[
            ['Attempts', `${run.attempts.length} of at most ${MAX_ATTEMPTS}`],
            ['Seconds waited', `${run.secondsWaited} s`],
          ]}
        />
      ) : null}
      <Panel label="Result">
        <pre aria-label="Result">{run ? run.result : '(not run yet)'}</pre>
      </Panel>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function OpsFallbackLab() {
  const [state, setState] = useState(initialFallback);
  const act = (action: FallbackAction) =>
    setState((current) => fallbackTransition(current, action));
  const status = modelStatus(state);
  const panel = fallbackPanel(state);
  const summary = fallbackSummary(state);
  return (
    <Lab
      title="What does Maya see when the model keeps failing?"
      eyebrow="Operations · the log as a health check"
      className="rel-lab ops-lab"
    >
      <Predict>
        How many failures pause the panel? Press Ask (the model fails) three times, then set Minutes
        later to 6.
      </Predict>
      <div className="cb-controls">
        <Button size="sm" variant="secondary" onClick={() => act({ type: 'ask', ok: false })}>
          Ask (the model fails)
        </Button>
        <Button size="sm" onClick={() => act({ type: 'ask', ok: true })}>
          Ask (the model answers)
        </Button>
        <label>
          Minutes later
          <ModelSelect
            aria-label="Minutes later"
            value={String(state.later)}
            onValueChange={(value) => act({ type: 'later', value: toLaterMinutes(value) })}
            options={LATER_MINUTES.map((minutes) => ({
              value: String(minutes),
              label: String(minutes),
            }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="GET /api/model/status">
          <pre aria-label="Status">{statusJson(status)}</pre>
        </Panel>
        <Table
          label="Calls"
          columns={['Call', 'Minute', 'Answer', 'Counted']}
          rows={state.calls.map((call, index) => [
            index + 1,
            call.minute,
            call.ok ? 'answered' : 'failed',
            yesNo(countedAt(call, state.later)),
          ])}
        />
      </div>
      <Facts
        values={[
          ['Recent failures', String(recentFailures(state))],
          ['Ask for suggestions button', panel.askDisabled ? 'disabled' : 'enabled'],
          ['Tasks added by hand', String(state.handTasks.length)],
        ]}
      />
      <div className="cb-two">
        <Panel label="The suggestions panel">
          <div className="ops-mock">
            <div>
              <Button
                size="sm"
                disabled={panel.askDisabled}
                onClick={() => act({ type: 'ask', ok: true })}
              >
                Ask for suggestions
              </Button>
            </div>
            <p aria-label="Panel status">{panel.statusLine}</p>
          </div>
        </Panel>
        <Panel label="Add task">
          <div className="ops-mock">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                act({ type: 'add-task' });
              }}
            >
              <label>
                Title
                <input
                  type="text"
                  value={state.draft}
                  placeholder="Send the agenda by Friday"
                  disabled={!panel.addTaskEnabled}
                  onChange={(event) => act({ type: 'draft', value: event.target.value })}
                />
              </label>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => act({ type: 'add-task' })}
              >
                Add task
              </Button>
            </form>
            <p>
              <small>{ADD_TASK_NOTE}</small>
            </p>
            <pre aria-label="Tasks added by hand">{bullets(state.handTasks)}</pre>
          </div>
        </Panel>
      </div>
      <Result tone={summary.tone}>{summary.message}</Result>
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
